import { Gender } from "@/features/auth/types";
import { DeclarativeIdentity } from "@/features/flow/types";
import { FullCountry, NaturalPerson, UserType } from "@/features/user/types";
import { SignupState } from "@/models/signup";

export const mapSignupStateToNp = (
  signupState: SignupState,
  countryOfCitizenship?: number | undefined
) => {
  const { phone } = signupState;
  const {
    person,
    addressBirth,
    occupation,
    sourcesIncome,
    monthlyIncome,
    addressResidence,
    taxAdress
  } = signupState.identity ?? {};
  const { gender, lastnames, firstnames, otherfirstnames, commonFirstName, commonName } =
    person ?? {};
  const stateOfBirth = addressBirth?.zip
    ? addressBirth?.cityOfBirth + " (" + addressBirth?.zip + ")"
    : addressBirth?.cityOfBirth;
  const np: Partial<NaturalPerson> = {
    FirstName: firstnames ?? "",
    LastName: lastnames ?? "",
    OtherFirstNames: otherfirstnames,
    CommonFirstName: commonFirstName,
    CommonName: commonName,
    Gender: gender ?? "",
    StateOfBirth: stateOfBirth ?? undefined,
    countryOfBirth: addressBirth?.countryCode
      ? Number.parseInt(addressBirth?.countryCode)
      : undefined,
    DateOfBirth: { date: addressBirth?.birth?.toString() ?? "" },
    CountryOfCitizenship: countryOfCitizenship,
    CSPCode: occupation?.CSPcode ?? undefined,
    ActivityCode: occupation?.activity ? Number.parseInt(occupation?.activity) : undefined,
    sourcesIncome: sourcesIncome,
    maximumMonthlyIncome: monthlyIncome?.maximum,
    minimumMonthlyIncome: monthlyIncome?.minimum,
    Address: addressResidence
      ? [
          {
            Address1: addressResidence.addressLine ?? "",
            ZipCode: addressResidence.zip ?? "",
            Country: addressResidence.countryCode ?? "",
            City: addressResidence.city ?? "",
            Tag: "Résidence principale",
            language: "",
            isPrincipalAddress: true
          }
        ]
      : undefined,
    AliasMobile: [
      {
        UUID: "",
        Owner: "",
        Public: false,
        MobileIndicatif: phone?.countryCode ?? "",
        Phone: phone?.phone ?? "",
        MobileNumber: phone?.phone ?? "",
        Main: false
      }
    ],
    TaxAddress: taxAdress
  };
  return np;
};
export const mapUserTypeToDeclarativeIdentity = (
  data: UserType | undefined,
  countries: FullCountry[] | undefined
): DeclarativeIdentity => {
  const getCountryAlpha2ById = (id: number | undefined) => {
    if (!id) return null;
    return (countries ?? []).find((country) => country.id == id)?.alpha2;
  };

  const np = data?.naturalPerson;
  const identity: DeclarativeIdentity = {
    person: {
      gender: np?.Gender as Gender,
      firstnames: np?.FirstName,
      lastnames: np?.LastName,
      commonFirstName: np?.CommonFirstName,
      commonName: np?.CommonName,
      otherfirstnames: np?.OtherFirstNames,
      nationalityCodes: [
        getCountryAlpha2ById(data?.naturalPerson?.CountryOfCitizenship) ?? "",
        getCountryAlpha2ById(data?.naturalPerson?.CountryOfCitizenship2 ?? undefined) ?? "",
        getCountryAlpha2ById(data?.naturalPerson?.CountryOfCitizenship3 ?? undefined) ?? "",
        getCountryAlpha2ById(data?.naturalPerson?.CountryOfCitizenship4 ?? undefined) ?? ""
      ].filter((a) => a !== "")
    },

    sourcesIncome: np?.sourcesIncome,
    monthlyIncome: {
      maximum: np?.maximumMonthlyIncome ?? null,
      minimum: np?.minimumMonthlyIncome ?? null,
      trans_key: ""
    },
    occupation: {
      activity: np?.ActivityCode ? np?.ActivityCode?.toString() : null,
      CSPcode: np?.CSPCode,
      description: null
    },
    addressBirth: {
      cityOfBirth: np?.StateOfBirth,
      countryCode: np?.countryOfBirth ? np?.countryOfBirth?.toString() : null,
      birth: np?.DateOfBirth?.date ? new Date(np?.DateOfBirth.date) : null
    }
  };
  return identity;
};

export const mapNpToSignupState = (
  {
    sourcesIncome,
    FirstName,
    LastName,
    OtherFirstNames,
    CommonFirstName,
    CommonName,
    Gender,
    ActivityCode,
    CSPCode,
    description,
    StateOfBirth,
    countryOfBirth,
    DateOfBirth,
    maximumMonthlyIncome,
    minimumMonthlyIncome,
    Address,
    TaxAddress
  }: NaturalPerson,
  nationalityCodes: string[]
): SignupState => {
  const declarativeIdentity: DeclarativeIdentity = {
    sourcesIncome: sourcesIncome,
    monthlyIncome: {
      maximum: maximumMonthlyIncome ?? null,
      minimum: minimumMonthlyIncome ?? null,
      trans_key: ""
    },
    person: {
      firstnames: FirstName,
      lastnames: LastName,
      otherfirstnames: OtherFirstNames,
      gender: Gender as Gender,
      commonFirstName: CommonFirstName,
      commonName: CommonName,
      nationalityCodes
    },
    occupation: {
      activity: ActivityCode ? ActivityCode?.toString() : null,
      CSPcode: CSPCode,
      description: description ?? null
    },
    addressResidence:
      Address.length > 0
        ? {
            addressLine: Address[0].Address1,
            zip: Address[0].ZipCode,
            city: Address[0].City,
            countryCode: Address[0].ZipCode
          }
        : undefined,
    addressBirth: {
      cityOfBirth: StateOfBirth,
      countryCode: countryOfBirth ? countryOfBirth?.toString() : null,
      birth: DateOfBirth?.date ? new Date(DateOfBirth.date) : null
    },
    taxAdress: TaxAddress
  };
  const signupState = {
    jwt_otp: null,
    mirror_uuid: null,
    verify_email: false,
    phone: null,
    cguItem: null,
    signup_type: null,
    security: {
      isWebauthn: false,
      password: null
    },
    identity: declarativeIdentity,
    taxAddress: TaxAddress ?? [],
    withPhone: false,
    isLaymoonFlowGoingBackFromPasswordStep: false
  };
  return signupState;
};
